// Import necessary modules from Redux Toolkit
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_URL } from '../../api/constants';
import { axiosPrivate } from '../../api/axios';

// Initial state for the HL7 logs slice
const initialState = {
  hl7Logs: [],
  loading: false,
  selectedLogId: null,
  error: null,
  isHL7EventLoading: false,
  hl7EventError: null,
  hl7EventDetails: null,
  isHL7EventUnlocked: false,
  hasHl7LogAccess: false,
};

// Async thunk to fetch HL7 logs data
export const fetchHL7Logs = createAsyncThunk(
  'hl7Logs/fetchHL7Logs',
  async (
    { axiosInstance = axiosPrivate, path = `${API_URL.HL7_HISTORY_LOGS}?pageNo=0&pageSize=250` },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(path);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch HL7 logs');
    }
  }
);

// Helper function to handle API errors
const handleApiError = (error, logId) => ({
  message: error.response?.data || 'Failed to fetch HL7 event details',
  logId,
  status: error.response?.status || 500,
  isUnlocked: error.response?.data?.isUnlocked || false,
});

// Async thunk to fetch HL7 event details
export const fetchHL7EventDetails = createAsyncThunk(
  'hl7Logs/fetchHL7EventDetails',
  async ({ axiosInstance = axiosPrivate, logId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_URL.HL7_HISTORY_LOGS}/${logId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleApiError(error, logId));
    }
  }
);

// HL7 logs slice
const hl7LogSlice = createSlice({
  name: 'hl7Log',
  initialState,
  reducers: {
    clearHL7Logs(state) {
      state.hl7Logs = [];
      state.selectedLogId = null;
      state.isHL7EventUnlocked = false;
      state.hl7EventDetails = null;
      state.isHL7EventLoading = false;
      state.hl7EventError = null;
    },
    clearHL7EventDetails(state) {
      state.hl7EventDetails = null;
      state.isHL7EventUnlocked = false;
      state.isHL7EventLoading = false;
      state.hl7EventError = null;
      state.selectedLogId = null;
    },
    setSelectedLogId(state, action) {
      state.selectedLogId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHL7Logs.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.hl7Logs = [];
        state.selectedLogId = null;
        state.isHL7EventUnlocked = false;
        state.hl7EventDetails = null;
        state.isHL7EventLoading = false;
        state.hl7EventError = null;
        state.hasHl7LogAccess = false;
      })
      .addCase(fetchHL7Logs.fulfilled, (state, action) => {
        state.loading = false;
        state.hl7Logs = action.payload;
        state.hasHl7LogAccess = true;
      })
      .addCase(fetchHL7Logs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.hasHl7LogAccess = false;
      })
      .addCase(fetchHL7EventDetails.pending, (state) => {
        state.isHL7EventLoading = true;
        state.hl7EventError = null;
        state.hl7EventDetails = null;
        state.isHL7EventUnlocked = false;
        state.hasHl7LogAccess = false;
      })
      .addCase(fetchHL7EventDetails.fulfilled, (state, action) => {
        state.isHL7EventLoading = false;
        state.hl7EventDetails = action.payload;
        state.isHL7EventUnlocked = true;
        state.hasHl7LogAccess = true;
      })
      .addCase(fetchHL7EventDetails.rejected, (state, action) => {
        state.isHL7EventLoading = false;
        state.hasHl7LogAccess = false;
        state.hl7EventError = action.payload;
      });
  },
});

// Export actions and reducer
export const hl7LogActions = hl7LogSlice.actions;
export const hl7LogReducer = hl7LogSlice.reducer;

// Selectors
export const getHL7Logs = (state) => state.hl7Log.hl7Logs;
export const getHL7LogsLoading = (state) => state.hl7Log.loading;
export const getHL7LogsError = (state) => state.hl7Log.error;
export const getSelectedLogId = (state) => state.hl7Log.selectedLogId;
export const getSelectedLog = (state, logId) =>
  state.hl7Log.hl7Logs?.listOfLogs?.find((log) => log.id === logId);
export const getHL7EventDetails = (state) => state.hl7Log.hl7EventDetails?.hl7EventLog;
export const getHL7EventLoading = (state) => state.hl7Log.isHL7EventLoading;
export const getHL7EventError = (state) => state.hl7Log.hl7EventError;
export const isHL7EventUnlocked = (state) => state.hl7Log.isHL7EventUnlocked;
export const getHasHl7LogAccess = (state) => state.hl7Log.hasHl7LogAccess;
